// components/CreativeRequestModal.tsx
import React from "react";
import {
  Modal,
  Box,
  Typography,
  Divider,
  Button,
  Badge,
  Chip,
} from "@mui/material";
import { CreativeRequest } from "../../context/types";

const modalStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  maxWidth: "800px",
  maxHeight: "90vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  overflowY: "auto",
};

interface CreativeRequestModalProps {
  open: boolean;
  onClose: () => void;
  creativeRequest: CreativeRequest | null;
  requesterName: string;
}

const CreativeRequestModal: React.FC<CreativeRequestModalProps> = ({
  open,
  onClose,
  creativeRequest,
  requesterName,
}) => {
  if (!creativeRequest) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Typography variant="h5" gutterBottom>
          Creative Request Details
        </Typography>
        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1">General Information</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
            <Chip label={`Type: ${creativeRequest.type}`} variant="outlined" />
            <Chip
              label={`Creative Type: ${creativeRequest.creativeType}`}
              variant="outlined"
            />
            <Chip
              label={`Header Type: ${creativeRequest.headerType}`}
              variant="outlined"
            />
            <Chip
              label={`Priority: ${creativeRequest.priority}`}
              color={
                creativeRequest.priority === "high"
                  ? "error"
                  : creativeRequest.priority === "medium"
                  ? "warning"
                  : "success"
              }
            />
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1">Brand Details</Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            <strong>Brand:</strong> {creativeRequest.brand}
          </Typography>
          <Typography variant="body1">
            <strong>Brand Description:</strong>{" "}
            {creativeRequest.brandDescription}
          </Typography>
          <Typography variant="body1">
            <strong>Ad Description:</strong> {creativeRequest.adDescription}
          </Typography>
          <Typography variant="body1">
            <strong>Country:</strong> {creativeRequest.country}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1">Script Content</Typography>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Hooks</Typography>
            {creativeRequest.hooks.map((hook, index) => (
              <Typography key={index} variant="body2" sx={{ mt: 1 }}>
                {hook}
              </Typography>
            ))}
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Bodies</Typography>
            {creativeRequest.bodies.map((body, index) => (
              <Typography key={index} variant="body2" sx={{ mt: 1 }}>
                {body}
              </Typography>
            ))}
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">CTAs</Typography>
            {creativeRequest.ctas.map((cta, index) => (
              <Typography key={index} variant="body2" sx={{ mt: 1 }}>
                {cta}
              </Typography>
            ))}
          </Box>
        </Box>

        {creativeRequest.notes && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Additional Notes</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {creativeRequest.notes}
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CreativeRequestModal;
